import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import CookiesManager from "@/components/CookiesManager";
import GmailAccountsManager from "@/components/GmailAccountsManager";
import Documentation from "@/components/Documentation";
import GoogleDataModal from "@/components/GoogleDataModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  LogOut,
  BarChart3,
  FileText,
  Bell,
  Menu,
  X,
  Cookie,
  Mail,
  Search,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Star,
  Camera,
  Database
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

const Dashboard = ({ page = "dashboard" }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showGoogleDataModal, setShowGoogleDataModal] = useState(false);
  const [selectedGoogleData, setSelectedGoogleData] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [loadingTest, setLoadingTest] = useState(false);
  const currentPage = page;
  
  // Estados para automações
  const [stats, setStats] = useState({
    total_searches: 0,
    total_valid_images: 0
  });
  const [automations, setAutomations] = useState([]);
  const [loadingAutomations, setLoadingAutomations] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPageAutomation, setCurrentPageAutomation] = useState(1);
  const [totalAutomations, setTotalAutomations] = useState(0);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
    }
  }, [navigate]);
  
  // Buscar estatísticas e automações
  useEffect(() => {
    if (currentPage === "dashboard") {
      fetchStats();
      fetchAutomations(currentPageAutomation);
      
      // Atualizar a cada 3 segundos
      const interval = setInterval(() => {
        fetchStats();
        fetchAutomations(currentPageAutomation);
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [currentPage, currentPageAutomation]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gmail/stats`, {
        headers: {
          "X-API-Key": API_KEY,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
    }
  };

  const fetchAutomations = async (page) => {
    try {
      // Só mostra loading na carga inicial
      if (isInitialLoad) {
        setLoadingAutomations(true);
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/gmail/automations?page=${page}&size=${ITEMS_PER_PAGE}`,
        {
          headers: {
            "X-API-Key": API_KEY,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAutomations(data.results);
        setTotalAutomations(data.total);
        setCurrentPageAutomation(page);
      }
    } catch (error) {
      console.error("Erro ao buscar automações:", error);
    } finally {
      setLoadingAutomations(false);
      setIsInitialLoad(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/");
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const openGoogleMapsLink = (googleId, type) => {
    const baseUrl = `https://www.google.com/maps/contrib/${googleId}`;
    const url = type === 'reviews' ? `${baseUrl}/reviews/` : `${baseUrl}/photos/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openGoogleDataModal = (automation) => {
    setSelectedGoogleData(automation);
    setShowGoogleDataModal(true);
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();
    
    if (!testEmail || !testEmail.includes('@')) {
      alert('Por favor, insira um email válido');
      return;
    }
    
    setLoadingTest(true);
    
    try {
      const token = localStorage.getItem("authToken");
      
      const response = await fetch(`${BACKEND_URL}/api/gmail/automate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email: testEmail }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowTestModal(false);
        setTestEmail("");
        setSelectedGoogleData(data);
        setShowGoogleDataModal(true);
      } else {
        const error = await response.json();
        alert(error.detail || 'Erro ao buscar dados do email');
      }
    } catch (error) {
      console.error('Erro ao testar email:', error);
      alert('Erro ao fazer requisição');
    } finally {
      setLoadingTest(false);
    }
  };

  const statsData = [
    {
      title: "Total de Buscas",
      value: stats.total_searches.toLocaleString(),
      icon: Search,
      color: "from-blue-500 to-blue-600",
    },
    {
      title: "Total de Imagens Válidas",
      value: stats.total_valid_images.toLocaleString(),
      icon: CheckCircle2,
      color: "from-green-500 to-green-600",
    },
  ];

  const totalPages = Math.ceil(totalAutomations / ITEMS_PER_PAGE);

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", active: currentPage === "dashboard", page: "dashboard", route: "/dashboard" },
    { icon: Cookie, label: "Cookies", active: currentPage === "cookies", page: "cookies", route: "/cookies" },
    { icon: Mail, label: "Contas Gmail", active: currentPage === "contas-gmail", page: "contas-gmail", route: "/contas-gmail" },
    { icon: FileText, label: "Documentação", active: currentPage === "documentacao", page: "documentacao", route: "/documentacao" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col lg:flex-row" data-testid="dashboard-page">
      {/* Sidebar Desktop */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 z-50 bg-[#1a1a1a] border-r border-gray-800 transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        }`}
      >
        <div className="flex flex-col h-full w-full overflow-hidden">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800 flex-shrink-0">
            {sidebarOpen && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-white p-1.5">
                  <img src="/gmail-icon.webp" alt="Gmail" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">Gmail APIs</h1>
                  <p className="text-xs text-gray-400">Painel de Controle</p>
                </div>
              </div>
            )}
            {!sidebarOpen && (
              <div className="flex w-full justify-center">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-white p-1">
                  <img src="/gmail-icon.webp" alt="Gmail" className="w-full h-full object-contain" />
                </div>
              </div>
            )}
          </div>

          {/* Menu Items - Com scroll */}
          <nav className="flex-1 py-6 px-3 space-y-2 overflow-y-auto">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.route)}
                  data-testid={`menu-item-${item.label.toLowerCase()}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    item.active
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-3 border-t border-gray-800 flex-shrink-0">
            <Button
              onClick={() => setShowLogoutDialog(true)}
              variant="outline"
              data-testid="logout-button"
              className={`w-full bg-red-500/10 border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300 ${
                !sidebarOpen && "justify-center px-2"
              }`}
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span className="ml-2">Sair</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="bg-[#1a1a1a] border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar saída</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Tem certeza que deseja sair do sistema? Você precisará fazer login novamente para acessar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogout}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Sair
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col overflow-hidden pb-20 lg:pb-0 transition-all duration-300 ${
        sidebarOpen ? "lg:ml-64" : "lg:ml-20"
      }`}>
        {/* Top Bar - Desktop only */}
        <header className="hidden lg:flex h-16 bg-[#1a1a1a] border-b border-gray-800 items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
              data-testid="toggle-sidebar"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <h2 className="text-xl font-bold text-white capitalize">
              {currentPage === "dashboard" ? "Dashboard" : 
               currentPage === "cookies" ? "Cookies" :
               currentPage === "contas-gmail" ? "Contas Gmail" :
               currentPage === "documentacao" ? "Documentação" : "Dashboard"}
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">Admin</p>
              <p className="text-xs text-gray-400">admin@gmail.com</p>
            </div>
            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
          </div>
        </header>

        {/* Mobile Header - Fixo */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 flex h-14 bg-[#1a1a1a] border-b border-gray-800 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-white p-1">
              <img src="/gmail-icon.webp" alt="Gmail" className="w-full h-full object-contain" />
            </div>
            <h2 className="text-lg font-bold text-white">Gmail APIs</h2>
          </div>
          <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-xs">A</span>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 pt-16 lg:pt-4">
          {currentPage === "dashboard" && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 lg:gap-4 mb-6">
                {statsData.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div
                      key={index}
                      className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden"
                      data-testid={`stat-card-${index}`}
                    >
                      {/* Background Glow Effect */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <Icon className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                            {stat.value}
                          </h3>
                          <p className="text-xs lg:text-sm text-gray-400 font-medium">
                            {stat.title}
                          </p>
                        </div>
                      </div>
                      
                      {/* Border Gradient */}
                      <div className={`absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br ${stat.color} blur-xl -z-10`}></div>
                    </div>
                  );
                })}
              </div>

              {/* Histórico de Automações */}
              <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg overflow-hidden">
                <div className="p-4 lg:p-6 border-b border-gray-800">
                  <h3 className="text-lg lg:text-xl font-bold text-white flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Histórico de Buscas
                  </h3>
                </div>

                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-800 hover:bg-gray-900">
                        <TableHead className="text-gray-400 w-20"></TableHead>
                        <TableHead className="text-gray-400">Email</TableHead>
                        <TableHead className="text-gray-400">ID Google</TableHead>
                        <TableHead className="text-gray-400">Ações</TableHead>
                        <TableHead className="text-gray-400">Data</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loadingAutomations && automations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                            Carregando...
                          </TableCell>
                        </TableRow>
                      ) : automations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                            Nenhuma busca realizada ainda
                          </TableCell>
                        </TableRow>
                      ) : (
                        automations.map((automation) => (
                          <TableRow
                            key={automation.id}
                            className="border-gray-800 hover:bg-gray-900"
                          >
                            <TableCell>
                              <div className="flex items-center justify-center">
                                {automation.profile_picture ? (
                                  <img
                                    src={automation.profile_picture}
                                    alt={automation.email}
                                    className="h-16 w-16 lg:h-20 lg:w-20 min-w-[4rem] lg:min-w-[5rem] rounded-full object-cover border-2 border-gray-700 cursor-pointer hover:border-blue-500 transition-colors flex-shrink-0"
                                    onClick={() => openImageModal(automation.profile_picture)}
                                  />
                                ) : (
                                  <div className="h-16 w-16 lg:h-20 lg:w-20 min-w-[4rem] lg:min-w-[5rem] rounded-full bg-gray-800 flex items-center justify-center border-2 border-gray-700 flex-shrink-0">
                                    <svg
                                      className="h-10 w-10 text-gray-500"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-white font-medium">
                              {automation.email}
                            </TableCell>
                            <TableCell className="text-gray-400 font-mono text-sm">
                              {automation.id_google || "-"}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1.5 flex-wrap">
                                <Button
                                  size="sm"
                                  onClick={() => automation.id_google && openGoogleMapsLink(automation.id_google, 'reviews')}
                                  disabled={!automation.id_google}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 h-7 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={automation.id_google ? "Ver Reviews no Google Maps" : "ID Google não disponível"}
                                >
                                  <Star className="h-3 w-3 mr-1" />
                                  Reviews
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => automation.id_google && openGoogleMapsLink(automation.id_google, 'photos')}
                                  disabled={!automation.id_google}
                                  className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 h-7 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={automation.id_google ? "Ver Photos no Google Maps" : "ID Google não disponível"}
                                >
                                  <Camera className="h-3 w-3 mr-1" />
                                  Photos
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => automation.dados_google && openGoogleDataModal(automation)}
                                  disabled={!automation.dados_google}
                                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 h-7 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={automation.dados_google ? "Ver Dados Completos do Google" : "Dados do Google não disponíveis"}
                                >
                                  <Database className="h-3 w-3 mr-1" />
                                  Ver Dados
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-400 text-sm">
                              {new Date(automation.timestamp).toLocaleString("pt-BR")}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden">
                  {loadingAutomations && automations.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      Carregando...
                    </div>
                  ) : automations.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      Nenhuma busca realizada ainda
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-800">
                      {automations.map((automation) => (
                        <div key={automation.id} className="p-4 space-y-3">
                          <div className="flex items-start gap-3">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              {automation.profile_picture ? (
                                <img
                                  src={automation.profile_picture}
                                  alt={automation.email}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-gray-700 cursor-pointer hover:border-blue-500 transition-colors"
                                  onClick={() => openImageModal(automation.profile_picture)}
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center border-2 border-gray-700">
                                  <svg
                                    className="h-7 w-7 text-gray-500"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">
                                {automation.email}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(automation.timestamp).toLocaleString("pt-BR")}
                              </p>
                              <div className="mt-2 space-y-2">
                                {automation.id_google && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">ID:</span>
                                    <span className="text-xs text-gray-400 font-mono">
                                      {automation.id_google}
                                    </span>
                                  </div>
                                )}
                                <div className="flex gap-2 flex-wrap">
                                  <Button
                                    size="sm"
                                    onClick={() => automation.id_google && openGoogleMapsLink(automation.id_google, 'reviews')}
                                    disabled={!automation.id_google}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 h-7 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={automation.id_google ? "Ver Reviews" : "ID Google não disponível"}
                                  >
                                    <Star className="h-3 w-3 mr-1" />
                                    Reviews
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => automation.id_google && openGoogleMapsLink(automation.id_google, 'photos')}
                                    disabled={!automation.id_google}
                                    className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 h-7 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={automation.id_google ? "Ver Photos" : "ID Google não disponível"}
                                  >
                                    <Camera className="h-3 w-3 mr-1" />
                                    Photos
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => automation.dados_google && openGoogleDataModal(automation)}
                                    disabled={!automation.dados_google}
                                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 h-7 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={automation.dados_google ? "Ver Dados" : "Dados não disponíveis"}
                                  >
                                    <Database className="h-3 w-3 mr-1" />
                                    Ver Dados
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t border-gray-800">
                    <p className="text-xs sm:text-sm text-gray-400">
                      Página {currentPageAutomation} de {totalPages} • {totalAutomations} busca(s)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAutomations(currentPageAutomation - 1)}
                        disabled={currentPageAutomation === 1}
                        className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAutomations(currentPageAutomation + 1)}
                        disabled={currentPageAutomation === totalPages}
                        className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {currentPage === "cookies" && <CookiesManager />}
          {currentPage === "contas-gmail" && <GmailAccountsManager />}
          {currentPage === "documentacao" && <Documentation />}
          
          {currentPage !== "dashboard" && currentPage !== "cookies" && currentPage !== "contas-gmail" && currentPage !== "documentacao" && (
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-2">Em Desenvolvimento</h3>
              <p className="text-gray-400">Esta seção estará disponível em breve.</p>
            </div>
          )}
        </main>
      </div>

      {/* Bottom Navigation - Mobile only */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#1a1a1a] border-t border-gray-800 z-50">
        <div className="grid grid-cols-4 h-16">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(item.route)}
                data-testid={`mobile-menu-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center justify-center gap-1 transition-colors ${
                  item.active
                    ? "text-blue-500"
                    : "text-gray-400"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Image Modal */}
      <AlertDialog open={showImageModal} onOpenChange={setShowImageModal}>
        <AlertDialogContent className="bg-[#1a1a1a] border-gray-800 max-w-2xl w-[95vw] sm:w-full p-0">
          <div className="flex items-center justify-center p-4">
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Profile"
                className="max-w-full max-h-96 rounded-lg object-contain"
              />
            )}
          </div>
          <AlertDialogFooter className="px-4 pb-4">
            <AlertDialogAction
              onClick={() => setShowImageModal(false)}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Google Data Modal */}
      {selectedGoogleData && (
        <GoogleDataModal
          open={showGoogleDataModal}
          onOpenChange={setShowGoogleDataModal}
          data={selectedGoogleData}
          email={selectedGoogleData.email}
          profilePicture={selectedGoogleData.profile_picture}
          nome={selectedGoogleData.nome}
        />
      )}

      {/* Botão Flutuante de Teste */}
      {currentPage === "dashboard" && (
        <button
          onClick={() => setShowTestModal(true)}
          className="fixed bottom-24 md:bottom-8 right-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-4 rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 flex items-center gap-2 z-50"
          title="Testar Email"
        >
          <Search className="h-5 w-5" />
          <span>Testar</span>
        </button>
      )}

      {/* Modal de Teste */}
      <AlertDialog open={showTestModal} onOpenChange={setShowTestModal}>
        <AlertDialogContent className="bg-[#1a1a1a] border-gray-800 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white text-xl">Testar Email</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Digite um e-mail para buscar os dados!
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <form onSubmit={handleTestEmail} className="py-4">
            <div className="space-y-2">
              <label htmlFor="test-email" className="text-sm font-medium text-gray-300">
                Email
              </label>
              <input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="exemplo@gmail.com"
                className="w-full px-4 py-3 bg-[#111] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={loadingTest}
              />
            </div>
          </form>
          
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-gray-800 text-white hover:bg-gray-700"
              disabled={loadingTest}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTestEmail}
              className="bg-blue-600 text-white hover:bg-blue-700"
              disabled={loadingTest}
            >
              {loadingTest ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Buscando...
                </span>
              ) : (
                'Buscar Dados'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
